import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  alpha,
  Button,
  Fade,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';

const NotFoundPage = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        boxSizing: 'border-box',
        // Layered background: base + soft gradient + very subtle pattern
        bgcolor: 'background.default',
        backgroundImage: theme => [
          `linear-gradient(165deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.04)} 50%, ${theme.palette.background.default} 100%)`,
          `radial-gradient(circle at 20% 30%, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 50%)`,
          `radial-gradient(circle at 80% 70%, ${alpha(theme.palette.primary.main, 0.04)} 0%, transparent 45%)`,
        ].join(', '),
        backgroundSize: '100% 100%, 100% 100%, 100% 100%',
        backgroundPosition: '0 0, 0 0, 0 0',
      }}
    >
      {/* Subtle dot pattern overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: theme => (theme.palette.mode === 'dark' ? 0.15 : 0.2),
          backgroundImage: theme =>
            `radial-gradient(circle at 1px 1px, ${alpha(theme.palette.primary.main, 0.35)} 1px, transparent 0)`,
          backgroundSize: '24px 24px',
          pointerEvents: 'none',
        }}
      />

      <Fade in={mounted} timeout={600}>
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            px: { xs: 2.5, sm: 3, md: 4 },
            py: { xs: 4, sm: 6 },
            maxWidth: 560,
          }}
        >
          {/* Hero number: 404 with strong display treatment */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'center',
              gap: { xs: 0.5, sm: 1 },
              mb: { xs: 2, sm: 3 },
              letterSpacing: '-0.04em',
            }}
          >
            {['4', '0', '4'].map((digit, i) => (
              <Typography
                key={digit + i}
                component="span"
                sx={{
                  fontFamily: 'StrawberrySansDisplay',
                  fontWeight: 400,
                  fontSize: { xs: '4.5rem', sm: '6rem', md: '7.5rem' },
                  lineHeight: 1,
                  color: 'text.primary',
                  textShadow: theme =>
                    theme.palette.mode === 'dark'
                      ? `0 0 80px ${alpha(theme.palette.primary.main, 0.25)}`
                      : `0 2px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                  opacity: 0.96,
                }}
              >
                {digit}
              </Typography>
            ))}
          </Box>

          {/* Overline */}
          <Typography
            variant="overline"
            sx={{
              fontFamily: 'StrawberrySansText',
              fontWeight: 600,
              fontSize: '0.7rem',
              letterSpacing: '0.2em',
              color: theme => alpha(theme.palette.primary.main, 0.9),
              textTransform: 'uppercase',
              mb: 1.5,
              display: 'block',
            }}
          >
            Page not found
          </Typography>

          {/* Headline */}
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'StrawberrySansDisplay',
              fontWeight: 400,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
              lineHeight: 1.3,
              color: 'text.primary',
              mb: 2,
              letterSpacing: '-0.02em',
            }}
          >
            This address doesn&apos;t have a reservation.
          </Typography>

          {/* Supporting copy */}
          <Typography
            variant="body1"
            sx={{
              fontFamily: 'StrawberrySansText',
              fontWeight: 400,
              fontSize: { xs: '0.95rem', sm: '1rem' },
              lineHeight: 1.6,
              color: theme => alpha(theme.palette.text.primary, 0.65),
              mb: 4,
            }}
          >
            The page you&apos;re looking for doesn&apos;t exist or may have been moved. Head back to the app to continue.
          </Typography>

          {/* CTA */}
          <Button
            variant="contained"
            size="large"
            startIcon={<ArrowBackRoundedIcon sx={{ fontSize: '1.25rem' }} />}
            onClick={() => navigate('/app')}
            sx={{
              fontFamily: 'StrawberrySansText',
              fontWeight: 600,
              fontSize: '1rem',
              textTransform: 'none',
              letterSpacing: '0.02em',
              px: 3.5,
              py: 1.75,
              borderRadius: 3,
              boxShadow: theme =>
                theme.palette.mode === 'dark'
                  ? `0 4px 20px ${alpha(theme.palette.primary.main, 0.35)}`
                  : `0 4px 14px ${alpha(theme.palette.primary.main, 0.25)}`,
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: theme =>
                  theme.palette.mode === 'dark'
                    ? `0 8px 28px ${alpha(theme.palette.primary.main, 0.4)}`
                    : `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
            }}
          >
            Back to app
          </Button>
        </Box>
      </Fade>
    </Box>
  );
};

export default NotFoundPage;
